import { AGENT_NAME } from './accountModel'
import carrie from './faces/carrie.webp'
import lupita from './faces/lupita.webp'
import tambrey from './faces/tambrey.webp'

/** The people behind the account. Bluey is always in the room; these join it. */
export enum Person {
  LUPITA = 'LUPITA',
  TAMBREY = 'TAMBREY',
  CARRIE = 'CARRIE',
}

/** Who said a line. Bluey speaks in every thread, so a person's is a three-way. */
export enum Speaker {
  YOU = 'YOU',
  BLUEY = 'BLUEY',
  PERSON = 'PERSON',
}

export interface Message {
  from: Speaker
  text: string
}

interface PersonProfile {
  /** A real face, where the prototype has one. */
  photo?: string
  name: string
  role: string
  initials: string
  messages: Message[]
}

/** What the agent opens with when it has something to say first. */
export const PROACTIVE_MESSAGE =
  'Your insurance down payment just went through. Coverage is now active, and available for review on your Insurance tab.'

/** Bluey alone, which is where the chat opens. */
export const AGENT_THREAD: Message[] = [
  { from: Speaker.YOU, text: 'Which coverage should I choose?' },
  {
    from: Speaker.BLUEY,
    text: 'Coverage recommendations need to be provided by a licensed insurance provider. Want me to bring this question to Lupita, your matched Cai broker?',
  },
  { from: Speaker.YOU, text: 'Yes' },
]

export const PEOPLE: Record<Person, PersonProfile> = {
  [Person.LUPITA]: {
    photo: lupita,
    name: 'Lupita',
    role: 'Insurance Broker',
    initials: 'L',
    messages: [
      {
        from: Speaker.BLUEY,
        text: 'Jim was curious which coverage is recommended for his fleet. I’ll let you and Lupita take it from here!',
      },
      {
        from: Speaker.PERSON,
        text: 'Happy to talk you through your options, Jim. Would you like to hop on the phone now, or keep chatting here?',
      },
      { from: Speaker.YOU, text: 'Phone would be great' },
      { from: Speaker.PERSON, text: 'Calling now!' },
    ],
  },
  [Person.TAMBREY]: {
    photo: tambrey,
    name: 'Tambrey',
    role: 'Bookkeeper',
    initials: 'T',
    messages: [
      { from: Speaker.PERSON, text: 'September close is done. Fuel was up 8% on August.' },
      { from: Speaker.YOU, text: 'anything i should look at?' },
      { from: Speaker.BLUEY, text: 'Two repairs are uncategorised. I can pull the receipts.' },
    ],
  },
  [Person.CARRIE]: {
    photo: carrie,
    name: 'Carrie',
    role: 'Give feedback',
    initials: 'C',
    messages: [
      {
        from: Speaker.PERSON,
        text: 'Hey I’m the product designer of Cai. Any ideas or place where you felt frustrated or confused?',
      },
      { from: Speaker.YOU, text: 'the nav took me a second' },
    ],
  },
}

export const PEOPLE_ORDER = [Person.LUPITA, Person.TAMBREY, Person.CARRIE]

/** The DOT the design system already uses as its sample. */
const SAMPLE_DOT = '4159108'

/**
 * Signing up, before the account or the name exist. The agent is still plain
 * "Cai" here — being named is the last thing that happens.
 */
export const ONBOARDING_THREAD: Message[] = [
  {
    from: Speaker.BLUEY,
    text: 'Hey I’m Cai, a personal assistant purpose built for running trucking fleets. Let’s take a few things off your plate.',
  },
  {
    from: Speaker.BLUEY,
    text: 'Enter your DOT number, and I’ll pull some basic information from the FMCSA about your business.',
  },
  { from: Speaker.YOU, text: SAMPLE_DOT },
  {
    from: Speaker.BLUEY,
    text: 'This look right?\n\nBlueline Freight\n2424 Dixie Highway,\nCincinnati Ohio, 45011',
  },
  { from: Speaker.YOU, text: 'Yep' },
  {
    from: Speaker.BLUEY,
    text: 'What would you like to name me? Does Blueline Freight have a mascot, or nickname?',
  },
]

export const AGENT = { name: AGENT_NAME, role: 'Your Cai agent' }

/** Straight after naming: the agent checks what it pulled, then proposes work. */
export const GETTING_STARTED_THREAD: Message[] = [
  { from: Speaker.BLUEY, text: 'CAB data suggests you have 5 trucks in your fleet. Is that right?' },
  { from: Speaker.YOU, text: 'Actually I’ve got 9' },
  {
    from: Speaker.BLUEY,
    text: 'Impressive! Here are a few things we can get started on today:\n\n• Update your FMCSA documentation, so that your next insurance purchase or renewal isn’t delayed by mismatching information.\n\n• Start getting your fleet audit ready, so that routine audits don’t result in a pile up of delays and fees\n\n• Review your current insurance to make sure it’s up to date and covering the risks you care about',
  },
]

/**
 * CFO opens on the cash-runway alert: the agent has read the bank balances,
 * the open AR and the pay cycles, and spotted a shortfall before it lands.
 */
/**
 * The same ground as CFO_SETUP_THREAD, with the product dissolved into the
 * agent: nothing to add, nothing to enable, no second thing being sold.
 */
export const CAI_AS_CFO_THREAD: Message[] = [
  {
    from: Speaker.BLUEY,
    text: 'I’m Cai. Think of me as the CFO for your fleet — I watch the money so you can watch the trucks.',
  },
  { from: Speaker.BLUEY, text: 'Have you ever worked with a CFO before?' },
  { from: Speaker.YOU, text: 'No, I’ve never had a Chief Financial Officer' },
  {
    from: Speaker.BLUEY,
    text:
      'A CFO helps your business grow by seeing a few weeks ahead, and suggesting smart financial decisions. ' +
      'Instead of managing invoicing, payroll, and bookkeeping tasks, think of your CFO like a strategic advisor. ' +
      'That is the part I do:\n\n' +
      '• Tell you which lanes still pay once fuel, tolls and settlements come out\n' +
      '• Warn you when a broker’s slow pay is about to squeeze payroll\n' +
      '• Say whether the next truck covers itself at today’s rates\n' +
      '• Flag a cash crunch days before it lands on a Friday',
  },
  {
    from: Speaker.BLUEY,
    text: 'Let’s start by connecting your business bank accounts, including any personal account where a few stray business transactions may have ended up.',
  },
  { from: Speaker.YOU, text: 'connected Huntington and my personal checking' },
  { from: Speaker.BLUEY, text: 'That’s the whole picture.' },
  { from: Speaker.BLUEY, text: 'What’s most important to you right now?' },
]

/** Setting CFO up: what it has to be told before it can forecast anything. */
export const CFO_SETUP_THREAD: Message[] = [
  { from: Speaker.BLUEY, text: 'Ready to add Chief Financial Officer capabilities to your Cai assistant?' },
  { from: Speaker.BLUEY, text: 'Have you ever worked with a CFO before?' },
  { from: Speaker.YOU, text: 'No, I’ve never had a Chief Financial Officer' },
  {
    from: Speaker.BLUEY,
    text:
      'A CFO helps your business grow by seeing a few weeks ahead, and suggesting smart financial decisions. ' +
      'Instead of managing invoicing, payroll, and bookkeeping tasks, think of your CFO like a strategic advisor. ' +
      'With CFO capabilities added, Cai will:\n\n' +
      '• Tell you which lanes still pay once fuel, tolls and settlements come out\n' +
      '• Warn you when a broker’s slow pay is about to squeeze payroll\n' +
      '• Say whether the next truck covers itself at today’s rates\n' +
      '• Flag a cash crunch days before it lands on a Friday',
  },
  {
    from: Speaker.BLUEY,
    text: 'Let’s start by ensuring all your business bank accounts are connected, including any personal account where a few stray business transactions may have ended up.',
  },
  { from: Speaker.YOU, text: 'connected Huntington and my personal checking' },
  { from: Speaker.BLUEY, text: 'That’s the whole picture.' },
  { from: Speaker.BLUEY, text: 'What’s most important to you right now?' },
]

export const CFO_THREAD: Message[] = [
  {
    from: Speaker.BLUEY,
    text: 'You’ll be about $8k short for driver settlements on Friday unless Broker X pays. They’re 12 days past their usual pay cycle.',
  },
  { from: Speaker.YOU, text: 'what are my options?' },
  {
    from: Speaker.BLUEY,
    text: 'Three. Chase Broker X today — I can send the invoice and the POD. Factor two of the open loads, which costs about $140. Or move settlements to Monday, when the Dollar General loads clear.',
  },
  { from: Speaker.YOU, text: 'chase them first' },
  {
    from: Speaker.BLUEY,
    text: 'Sent. I’ll watch for a reply and tell you by Thursday lunchtime if we need the factoring line instead.',
  },
]
